import { useEffect, useMemo, useState } from 'react';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import styles from './PublicCapacitacionesSelector.module.css';

const WEEK_DAYS = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
const WEEK_COLUMNS = [
  { value: 1, short: 'Lun', label: 'Lunes' },
  { value: 2, short: 'Mar', label: 'Martes' },
  { value: 3, short: 'Mie', label: 'Miercoles' },
  { value: 4, short: 'Jue', label: 'Jueves' },
  { value: 5, short: 'Vie', label: 'Viernes' },
  { value: 6, short: 'Sab', label: 'Sabado' },
  { value: 7, short: 'Dom', label: 'Domingo' },
];
const SLOT_MINUTES = 30;

function normalizeId(value) {
  const id = Number(value);
  return Number.isFinite(id) ? id : value;
}

function formatHora(value) {
  if (!value) return null;
  return String(value).slice(0, 5);
}

function fechaClave(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

function parseDateKey(value) {
  const key = fechaClave(value);
  if (!key) return null;
  const date = new Date(`${key}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatFecha(value, options = { day: 'numeric', month: 'short' }) {
  const date = parseDateKey(value);
  if (!date) return 'Fecha por definir';
  return new Intl.DateTimeFormat('es-CO', options).format(date);
}

function formatDiaFecha(value) {
  const date = parseDateKey(value);
  if (!date) return 'Fecha por definir';
  return `${WEEK_DAYS[date.getDay()]}, ${formatFecha(value)}`;
}

function formatRangoFechas(capacitacion) {
  const inicio = fechaClave(capacitacion.fecha_inicio);
  const fin = fechaClave(capacitacion.fecha_fin) || inicio;
  if (!inicio) return 'Fecha por definir';
  if (!fin || inicio === fin) return formatFecha(inicio, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${formatFecha(inicio)} - ${formatFecha(fin, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function formatRangoHora(inicio, fin) {
  const start = formatHora(inicio);
  const end = formatHora(fin);
  return start && end ? `${start} - ${end}` : 'Horario por definir';
}

function getNombre(capacitacion) {
  return capacitacion.nombre || capacitacion.convocatoria || capacitacion.nombre_convocatoria || `Capacitacion ${capacitacion.id}`;
}

function getConvocatoria(capacitacion) {
  const convocatoria = capacitacion.convocatoria || capacitacion.nombre_convocatoria;
  if (!convocatoria || convocatoria === capacitacion.nombre) return null;
  return convocatoria;
}

function dateToISO(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function isoDay(date) {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

function getWeekDayLabel(value, short = false) {
  const day = WEEK_COLUMNS.find((item) => item.value === Number(value));
  return day ? (short ? day.short : day.label) : 'Dia';
}

function toMinutes(value) {
  const [hours, minutes] = String(value || '').split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
}

function minutesToTime(value) {
  const safe = Math.max(0, Math.min(value, (23 * 60) + 59));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function floorToSlot(value) {
  return Math.floor(value / SLOT_MINUTES) * SLOT_MINUTES;
}

function ceilToSlot(value) {
  return Math.ceil(value / SLOT_MINUTES) * SLOT_MINUTES;
}

function buildSessionsFromBlocks(capacitacion, blocks = []) {
  const start = parseDateKey(capacitacion.fecha_inicio);
  const end = parseDateKey(capacitacion.fecha_fin) || start;
  if (!start || !end || end < start) return [];

  const sessions = [];
  for (let cursor = start; cursor <= end; cursor = addDays(cursor, 1)) {
    const day = isoDay(cursor);
    blocks.forEach((block) => {
      if (Number(block.dia_semana) !== day) return;
      sessions.push({
        fecha: dateToISO(cursor),
        fecha_inicio: dateToISO(cursor),
        fecha_fin: dateToISO(cursor),
        hora_inicio: block.hora_inicio,
        hora_fin: block.hora_fin,
        lugar: block.lugar || capacitacion.lugar || null,
      });
    });
  }
  return sessions;
}

function getScheduleEntries(capacitacion) {
  const sesiones = Array.isArray(capacitacion.sesiones) ? capacitacion.sesiones : [];
  const bloques = Array.isArray(capacitacion.horario_bloques) ? capacitacion.horario_bloques : [];

  if (sesiones.length) {
    return sesiones.map((sesion, index) => ({
      id: `${capacitacion.id}-sesion-${sesion.id || index}`,
      capacitacionId: normalizeId(capacitacion.id),
      nombre: getNombre(capacitacion),
      fecha_inicio: fechaClave(sesion.fecha || sesion.fecha_inicio),
      fecha_fin: fechaClave(sesion.fecha || sesion.fecha_inicio),
      hora_inicio: formatHora(sesion.hora_inicio),
      hora_fin: formatHora(sesion.hora_fin),
      lugar: sesion.lugar || capacitacion.lugar || null,
      isSession: true,
    }));
  }

  const generated = buildSessionsFromBlocks(capacitacion, bloques);
  if (generated.length) {
    return generated.map((sesion, index) => ({
      id: `${capacitacion.id}-bloque-${index}`,
      capacitacionId: normalizeId(capacitacion.id),
      nombre: getNombre(capacitacion),
      fecha_inicio: fechaClave(sesion.fecha_inicio),
      fecha_fin: fechaClave(sesion.fecha_fin),
      hora_inicio: formatHora(sesion.hora_inicio),
      hora_fin: formatHora(sesion.hora_fin),
      lugar: sesion.lugar || capacitacion.lugar || null,
      isSession: true,
    }));
  }

  return [{
    id: `${capacitacion.id}-general`,
    capacitacionId: normalizeId(capacitacion.id),
    nombre: getNombre(capacitacion),
    fecha_inicio: fechaClave(capacitacion.fecha_inicio),
    fecha_fin: fechaClave(capacitacion.fecha_fin) || fechaClave(capacitacion.fecha_inicio),
    hora_inicio: formatHora(capacitacion.hora_inicio),
    hora_fin: formatHora(capacitacion.hora_fin),
    lugar: capacitacion.lugar || null,
    isSession: false,
  }];
}

function mergeAdjacentWeeklyEntries(entries = []) {
  const sorted = entries
    .filter((entry) => entry.dia_semana && entry.hora_inicio && entry.hora_fin)
    .sort((a, b) => (
      Number(a.dia_semana) - Number(b.dia_semana)
      || String(a.capacitacionId).localeCompare(String(b.capacitacionId))
      || String(a.lugar || '').localeCompare(String(b.lugar || ''))
      || String(a.hora_inicio).localeCompare(String(b.hora_inicio))
      || String(a.hora_fin).localeCompare(String(b.hora_fin))
    ));

  return sorted.reduce((acc, entry) => {
    const last = acc[acc.length - 1];
    const sameBlock = last
      && last.capacitacionId === entry.capacitacionId
      && Number(last.dia_semana) === Number(entry.dia_semana)
      && String(last.lugar || '') === String(entry.lugar || '')
      && last.source === entry.source;

    if (sameBlock && last.hora_inicio === entry.hora_inicio && last.hora_fin === entry.hora_fin) {
      return acc;
    }

    if (sameBlock && toMinutes(entry.hora_inicio) <= toMinutes(last.hora_fin)) {
      const lastEnd = toMinutes(last.hora_fin);
      const entryEnd = toMinutes(entry.hora_fin);
      last.hora_fin = minutesToTime(Math.max(lastEnd, entryEnd));
      last.id = `${last.id}-${entry.id}`;
      return acc;
    }

    acc.push({ ...entry });
    return acc;
  }, []);
}

function getWeeklyEntries(capacitacion, source = 'selected') {
  const bloques = Array.isArray(capacitacion.horario_bloques) ? capacitacion.horario_bloques : [];
  const sesiones = Array.isArray(capacitacion.sesiones) ? capacitacion.sesiones : [];

  if (bloques.length) {
    return mergeAdjacentWeeklyEntries(bloques.map((block, index) => ({
      id: `${capacitacion.id}-weekly-block-${block.id || index}`,
      capacitacionId: normalizeId(capacitacion.id),
      nombre: getNombre(capacitacion),
      dia_semana: Number(block.dia_semana),
      hora_inicio: formatHora(block.hora_inicio),
      hora_fin: formatHora(block.hora_fin),
      lugar: block.lugar || capacitacion.lugar || null,
      source,
    })));
  }

  if (sesiones.length) {
    return mergeAdjacentWeeklyEntries(sesiones.map((sesion, index) => {
      const fecha = fechaClave(sesion.fecha || sesion.fecha_inicio);
      const date = parseDateKey(fecha);
      return {
        id: `${capacitacion.id}-weekly-session-${sesion.id || index}`,
        capacitacionId: normalizeId(capacitacion.id),
        nombre: getNombre(capacitacion),
        dia_semana: Number(sesion.dia_semana) || (date ? isoDay(date) : null),
        hora_inicio: formatHora(sesion.hora_inicio),
        hora_fin: formatHora(sesion.hora_fin),
        lugar: sesion.lugar || capacitacion.lugar || null,
        source,
      };
    }));
  }

  const date = parseDateKey(capacitacion.fecha_inicio);
  return mergeAdjacentWeeklyEntries([{
    id: `${capacitacion.id}-weekly-general`,
    capacitacionId: normalizeId(capacitacion.id),
    nombre: getNombre(capacitacion),
    dia_semana: date ? isoDay(date) : null,
    hora_inicio: formatHora(capacitacion.hora_inicio),
    hora_fin: formatHora(capacitacion.hora_fin),
    lugar: capacitacion.lugar || null,
    source,
  }]);
}

function hasCompleteSchedule(entry) {
  return Boolean(entry.fecha_inicio && entry.hora_inicio && entry.hora_fin);
}

function entriesOverlap(a, b) {
  if (!hasCompleteSchedule(a) || !hasCompleteSchedule(b)) return false;
  const finA = a.fecha_fin || a.fecha_inicio;
  const finB = b.fecha_fin || b.fecha_inicio;
  const fechasSeCruzan = a.fecha_inicio <= finB && finA >= b.fecha_inicio;
  const horasSeCruzan = a.hora_inicio < b.hora_fin && a.hora_fin > b.hora_inicio;
  return fechasSeCruzan && horasSeCruzan;
}

function describeEntry(entry) {
  const fecha = entry.fecha_inicio
    ? formatDiaFecha(entry.fecha_inicio)
    : 'fecha por definir';
  return `${fecha}, ${formatRangoHora(entry.hora_inicio, entry.hora_fin)}`;
}

function findFirstConflict(candidate, selectedCapacitaciones, existingCapacitaciones = []) {
  const candidateEntries = getScheduleEntries(candidate);
  const pools = [
    ...selectedCapacitaciones.map((capacitacion) => ({ capacitacion, type: 'selected' })),
    ...existingCapacitaciones.map((capacitacion) => ({ capacitacion, type: 'existing' })),
  ];

  for (const entry of candidateEntries) {
    for (const item of pools) {
      if (normalizeId(item.capacitacion.id) === normalizeId(candidate.id)) continue;
      const conflictEntry = getScheduleEntries(item.capacitacion).find((other) => entriesOverlap(entry, other));
      if (conflictEntry) {
        return {
          courseId: normalizeId(candidate.id),
          courseName: getNombre(candidate),
          conflictId: normalizeId(item.capacitacion.id),
          conflictName: getNombre(item.capacitacion),
          conflictType: item.type,
          entry,
          conflictEntry,
        };
      }
    }
  }
  return null;
}

function buildSelectedConflicts(selectedCapacitaciones, existingCapacitaciones = []) {
  const conflicts = [];

  selectedCapacitaciones.forEach((capacitacion, index) => {
    const nextSelected = selectedCapacitaciones.slice(index + 1);
    const selectedConflict = findFirstConflict(capacitacion, nextSelected, []);
    if (selectedConflict) conflicts.push(selectedConflict);

    const existingConflict = findFirstConflict(capacitacion, [], existingCapacitaciones);
    if (existingConflict) conflicts.push(existingConflict);
  });

  return conflicts;
}

function conflictToText(conflict) {
  if (!conflict) return '';
  const source = conflict.conflictType === 'existing' ? 'ya inscrita' : 'seleccionada';
  return `"${conflict.courseName}" se cruza con "${conflict.conflictName}" (${source}) el ${describeEntry(conflict.entry)}.`;
}

function getCardSummary(capacitacion) {
  const weeklyEntries = getWeeklyEntries(capacitacion);
  if (weeklyEntries.length === 1) {
    const entry = weeklyEntries[0];
    return `${getWeekDayLabel(entry.dia_semana)} ${formatRangoHora(entry.hora_inicio, entry.hora_fin)}`;
  }
  if (weeklyEntries.length > 1) return `${weeklyEntries.length} bloques semanales`;

  const entries = getScheduleEntries(capacitacion).filter(hasCompleteSchedule);
  if (!entries.length) return 'Horario por definir';
  if (entries.length === 1) return formatRangoHora(entries[0].hora_inicio, entries[0].hora_fin);
  const first = [...entries].sort((a, b) => (
    String(a.fecha_inicio).localeCompare(String(b.fecha_inicio))
    || String(a.hora_inicio).localeCompare(String(b.hora_inicio))
  ))[0];
  return `${entries.length} sesiones desde ${formatDiaFecha(first.fecha_inicio)}`;
}

function getVisibleWeekDays(entries = []) {
  const days = new Set(entries.map((entry) => Number(entry.dia_semana)).filter(Boolean));
  const includeWeekend = days.has(6) || days.has(7);
  return includeWeekend ? WEEK_COLUMNS : WEEK_COLUMNS.slice(0, 5);
}

function getWeeklyTimeSlots(entries = []) {
  const complete = entries.filter((entry) => entry.hora_inicio && entry.hora_fin);
  if (!complete.length) return [];

  const minStart = Math.min(...complete.map((entry) => toMinutes(entry.hora_inicio)).filter(Number.isFinite));
  const maxEnd = Math.max(...complete.map((entry) => toMinutes(entry.hora_fin)).filter(Number.isFinite));
  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd <= minStart) return [];

  const start = floorToSlot(minStart);
  const end = ceilToSlot(maxEnd);
  const slots = [];
  for (let cursor = start; cursor < end; cursor += SLOT_MINUTES) {
    slots.push(cursor);
  }
  return slots;
}

function getExistingPreviewLines(entries = []) {
  return entries
    .filter((entry) => entry.dia_semana && entry.hora_inicio && entry.hora_fin)
    .sort((a, b) => (
      Number(a.dia_semana) - Number(b.dia_semana)
      || String(a.hora_inicio).localeCompare(String(b.hora_inicio))
      || String(a.nombre).localeCompare(String(b.nombre))
    ));
}

const PublicCapacitacionesSelector = ({
  capacitaciones = [],
  selectedIds = [],
  existingCapacitaciones = [],
  onSelectedIdsChange,
  onConflictAttempt,
  emptyMessage = 'No hay capacitaciones disponibles para este evento.',
  helperText = 'Selecciona las capacitaciones en las que quieres participar.',
  serverConflict = null,
}) => {
  const [blockedConflict, setBlockedConflict] = useState(null);
  const selectedIdSet = useMemo(() => new Set(selectedIds.map(normalizeId)), [selectedIds]);
  const selectedCapacitaciones = useMemo(
    () => capacitaciones.filter((capacitacion) => selectedIdSet.has(normalizeId(capacitacion.id))),
    [capacitaciones, selectedIdSet],
  );
  const selectedConflicts = useMemo(
    () => buildSelectedConflicts(selectedCapacitaciones, existingCapacitaciones),
    [selectedCapacitaciones, existingCapacitaciones],
  );
  const agendaEntries = useMemo(
    () => selectedCapacitaciones.flatMap((capacitacion) => getWeeklyEntries(capacitacion, 'selected')),
    [selectedCapacitaciones],
  );
  const existingEntries = useMemo(
    () => existingCapacitaciones.flatMap((capacitacion) => getWeeklyEntries(capacitacion, 'existing')),
    [existingCapacitaciones],
  );
  const visibleWeekDays = useMemo(() => getVisibleWeekDays(agendaEntries), [agendaEntries]);
  const timeSlots = useMemo(() => getWeeklyTimeSlots(agendaEntries), [agendaEntries]);
  const existingPreviewLines = useMemo(() => getExistingPreviewLines(existingEntries), [existingEntries]);
  const serverConflictIds = useMemo(() => (
    new Set((serverConflict?.capacitaciones || []).map((item) => normalizeId(item.id)))
  ), [serverConflict]);

  useEffect(() => {
    if (!selectedConflicts.length) return;
    setBlockedConflict(selectedConflicts[0]);
  }, [selectedConflicts]);

  const conflictCards = new Set();
  selectedConflicts.forEach((conflict) => {
    conflictCards.add(conflict.courseId);
    conflictCards.add(conflict.conflictId);
  });
  if (blockedConflict) {
    conflictCards.add(blockedConflict.courseId);
    conflictCards.add(blockedConflict.conflictId);
  }
  serverConflictIds.forEach((id) => conflictCards.add(id));

  const toggleCapacitacion = (capacitacion, checked) => {
    const id = normalizeId(capacitacion.id);
    setBlockedConflict(null);

    if (!checked) {
      onSelectedIdsChange(selectedIds.map(normalizeId).filter((current) => current !== id));
      return;
    }

    const selectedWithoutCurrent = selectedCapacitaciones.filter((item) => normalizeId(item.id) !== id);
    const conflict = findFirstConflict(capacitacion, selectedWithoutCurrent, existingCapacitaciones);
    if (conflict) {
      setBlockedConflict(conflict);
      onConflictAttempt?.(conflictToText(conflict));
      return;
    }

    onSelectedIdsChange([...selectedIds.map(normalizeId), id]);
  };

  const visibleConflict = blockedConflict || selectedConflicts[0];

  return (
    <section className={styles.selector}>
      <div className={styles.selectorHeader}>
        <div>
          <h4>Capacitaciones disponibles</h4>
          <p>{helperText}</p>
        </div>
        <span className={styles.selectedPill}>{selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}</span>
      </div>

      {(visibleConflict || serverConflict) && (
        <div className={styles.conflictBanner} role="alert">
          <ExclamationTriangleIcon width={20} />
          <div>
            <strong>Hay un cruce de horario</strong>
            <span>
              {visibleConflict
                ? conflictToText(visibleConflict)
                : 'El servidor encontro un cruce al validar la inscripcion. Revisa las capacitaciones marcadas.'}
            </span>
          </div>
        </div>
      )}

      <div className={styles.capacitacionesLayout}>
        <div className={styles.courseList}>
          {capacitaciones.length === 0 ? (
            <p className={styles.emptyState}>{emptyMessage}</p>
          ) : capacitaciones.map((capacitacion) => {
            const id = normalizeId(capacitacion.id);
            const selected = selectedIdSet.has(id);
            const sinCupo = capacitacion.cupo_total != null && Number(capacitacion.cupos_disponibles) <= 0;
            const hasConflict = conflictCards.has(id);
            const convocatoria = getConvocatoria(capacitacion);

            return (
              <label
                key={capacitacion.id}
                className={[
                  styles.courseCard,
                  selected ? styles.courseSelected : '',
                  sinCupo ? styles.courseDisabled : '',
                  hasConflict ? styles.courseConflict : '',
                ].filter(Boolean).join(' ')}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={sinCupo}
                  onChange={(event) => toggleCapacitacion(capacitacion, event.target.checked)}
                />
                <div className={styles.courseBody}>
                  <div className={styles.courseTitleRow}>
                    <div>
                      <strong>{getNombre(capacitacion)}</strong>
                      {convocatoria && <span>{convocatoria}</span>}
                    </div>
                    {selected && <CheckCircleIcon width={20} className={styles.checkIcon} />}
                  </div>

                  <div className={styles.badgeRow}>
                    {capacitacion.modalidad && <span className={styles.badge}>{capacitacion.modalidad}</span>}
                    <span className={styles.badgeMuted}>{formatRangoFechas(capacitacion)}</span>
                    {capacitacion.cupo_total != null && (
                      <span className={styles.badgeMuted}>
                        {Math.max(Number(capacitacion.cupos_ocupados || 0), 0)}/{capacitacion.cupo_total} cupos
                      </span>
                    )}
                  </div>

                  <div className={styles.metaGrid}>
                    <span><ClockIcon width={15} /> {getCardSummary(capacitacion)}</span>
                    <span><MapPinIcon width={15} /> {capacitacion.lugar || 'Lugar por definir'}</span>
                    {capacitacion.lider_nombre && <span><UsersIcon width={15} /> Lider: {capacitacion.lider_nombre}</span>}
                  </div>

                  {hasConflict && (
                    <p className={styles.cardWarning}>Esta capacitacion participa en un cruce de horario.</p>
                  )}
                  {sinCupo && (
                    <p className={styles.cardWarning}>No hay cupos disponibles.</p>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        <aside className={styles.agendaPanel} aria-label="Horario seleccionado">
          <div className={styles.agendaHeader}>
            <span>Tu horario seleccionado</span>
            <strong>{selectedIds.length} curso{selectedIds.length === 1 ? '' : 's'}</strong>
          </div>

          {!agendaEntries.length || !timeSlots.length ? (
            <div className={styles.agendaEmpty}>
              <CalendarDaysIcon width={28} />
              <p>
                {selectedIds.length
                  ? 'Las capacitaciones seleccionadas aun no tienen horario completo.'
                  : 'Selecciona una capacitacion para ver aqui tu horario semanal.'}
              </p>
            </div>
          ) : (
            <div className={styles.weekScheduleScroll}>
              <div
                className={styles.weekSchedule}
                style={{
                  gridTemplateColumns: `48px repeat(${visibleWeekDays.length}, minmax(58px, 1fr))`,
                  gridTemplateRows: `32px repeat(${timeSlots.length}, 28px)`,
                }}
              >
                <div className={`${styles.weekHead} ${styles.weekTimeHead}`}>Hora</div>
                {visibleWeekDays.map((day, index) => (
                  <div
                    key={day.value}
                    className={styles.weekHead}
                    style={{ gridColumn: index + 2, gridRow: 1 }}
                  >
                    {day.short}
                  </div>
                ))}

                {timeSlots.map((slot, rowIndex) => (
                  <div
                    key={`time-${slot}`}
                    className={styles.weekTimeCell}
                    style={{ gridColumn: 1, gridRow: rowIndex + 2 }}
                  >
                    {slot % 60 === 0 ? minutesToTime(slot) : ''}
                  </div>
                ))}

                {timeSlots.flatMap((slot, rowIndex) => (
                  visibleWeekDays.map((day, dayIndex) => (
                    <div
                      key={`${day.value}-${slot}`}
                      className={styles.weekSlot}
                      style={{ gridColumn: dayIndex + 2, gridRow: rowIndex + 2 }}
                    />
                  ))
                ))}

                {agendaEntries.map((entry) => {
                  const dayIndex = visibleWeekDays.findIndex((day) => day.value === Number(entry.dia_semana));
                  const start = floorToSlot(toMinutes(entry.hora_inicio));
                  const end = ceilToSlot(toMinutes(entry.hora_fin));
                  const rowIndex = timeSlots.indexOf(start);
                  if (dayIndex < 0 || rowIndex < 0 || end <= start) return null;

                  return (
                    <div
                      key={entry.id}
                      className={styles.weekBlock}
                      style={{
                        gridColumn: dayIndex + 2,
                        gridRow: `${rowIndex + 2} / span ${Math.max(1, (end - start) / SLOT_MINUTES)}`,
                      }}
                      title={`${getWeekDayLabel(entry.dia_semana)} ${formatRangoHora(entry.hora_inicio, entry.hora_fin)} - ${entry.nombre}`}
                    >
                      <strong>{formatRangoHora(entry.hora_inicio, entry.hora_fin)}</strong>
                      <span>{entry.nombre}</span>
                      <small>{entry.lugar || 'Lugar por definir'}</small>
                    </div>
                  );
                })}
              </div>
              <div className={styles.weekLegend}>
                {agendaEntries.map((entry) => (
                  <span key={`legend-${entry.id}`}>
                    <strong>{getWeekDayLabel(entry.dia_semana, true)} {formatRangoHora(entry.hora_inicio, entry.hora_fin)}</strong>
                    {' '}{entry.nombre}
                  </span>
                ))}
              </div>
            </div>
          )}

          {existingCapacitaciones.length > 0 && (
            <div className={styles.existingBox}>
              <div className={styles.existingHeader}>
                <UsersIcon width={16} />
                <span>Ya inscritas</span>
              </div>
              {existingPreviewLines.length === 0 ? (
                <p>Hay capacitaciones previas sin horario completo.</p>
              ) : (
                <div className={styles.existingList}>
                  {existingPreviewLines.slice(0, 5).map((entry) => (
                    <span key={`existing-${entry.id}`}>
                      <strong>{getWeekDayLabel(entry.dia_semana, true)} {formatRangoHora(entry.hora_inicio, entry.hora_fin)}</strong>
                      {entry.nombre}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </section>
  );
};

export default PublicCapacitacionesSelector;
